import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import logo from '../../assets/logo.svg'
import './StaticPage.css'
import { useAuth } from '../hooks/useAuth'
import { toastStore } from '../notifications/toast-store'
import { httpClient } from '../../api/http'
import { connectionStateService, type SyncStatus } from '../../core/infrastructure/connection-state-service'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'

type FeedbackType = 'feedback' | 'bug' | 'feature'

export function Feedback() {
  const navigate = useNavigate()
  const { user, userDetails, login } = useAuth()

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('feedback')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(connectionStateService.getSyncStatus())

  useEffect(() => {
    return connectionStateService.onStatusChange(setSyncStatus)
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!user) {
      toastStore.push({ message: 'Please log in to send feedback.', type: 'error' })
      return
    }

    if (syncStatus !== 'online') {
      toastStore.push({ message: 'Feedback can only be sent while you are online.', type: 'error' })
      return
    }

    const trimmed = message.trim()
    if (!trimmed) {
      toastStore.push({ message: 'Please enter your feedback before sending.', type: 'error' })
      return
    }

    setSubmitting(true)
    try {
      await httpClient.post<void>('/api/feedback', {
        feedbackType,
        message: trimmed,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent
      })

      toastStore.push({ message: 'Thanks — your feedback has been sent.', type: 'success' })
      setMessage('')
      setFeedbackType('feedback')
    } catch (err: unknown) {
      const error = err as any
      console.error('Failed to send feedback', error)
      if (error?.status === 429) {
        toastStore.push({ message: 'You’ve sent a lot of feedback recently. Please try again later.', type: 'error' })
      } else if (error?.status === 401) {
        toastStore.push({ message: 'Please log in to send feedback.', type: 'error' })
      } else {
        toastStore.push({ message: 'Sorry — we could not send feedback right now.', type: 'error' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="static-page">
      <header className="static-header">
        <div className="static-header-content">
          <img
            src={logo}
            alt="Ordelio Logo"
            className="static-logo clickable"
            onClick={() => navigate('/')}
          />
          <button onClick={() => navigate('/')} className="back-to-home-btn">
            ← Back to Home
          </button>
        </div>
      </header>

      <PwaUpdateBanner />

      <main className="static-content">
        <article className="content-container">
          <h1>Feedback</h1>
          <p className="last-updated">We read every message. Please don’t include sensitive personal info.</p>

          <section className="content-section">
            <h2>Send feedback</h2>

            {!user && (
              <div className="feedback-signed-in">
                <p>You need to be logged in to send feedback.</p>
                <button onClick={login} className="cta-button">Login with Google</button>
              </div>
            )}

            {user && syncStatus !== 'online' && (
              <p className="feedback-signed-in">
                {syncStatus === 'offline'
                  ? 'You’re offline. Connect to the internet to send feedback.'
                  : 'The server is currently unreachable. Try again in a moment.'}
              </p>
            )}

            <form className="feedback-form" onSubmit={onSubmit}>
              <div className="feedback-field">
                <label className="feedback-label" htmlFor="feedback-type">Type</label>
                <select
                  id="feedback-type"
                  className="feedback-select"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                  disabled={submitting || !user}
                >
                  <option value="feedback">Feedback</option>
                  <option value="bug">Bug</option>
                  <option value="feature">Feature request</option>
                </select>
              </div>

              {user && (
                <p className="feedback-signed-in">
                  Signed in as <strong>{userDetails?.email || user.userDetails || 'Loading...'}</strong>
                </p>
              )}

              <div className="feedback-field">
                <label className="feedback-label" htmlFor="feedback-message">Message</label>
                <textarea
                  id="feedback-message"
                  className="feedback-textarea"
                  rows={6}
                  placeholder="What’s working well? What’s confusing? What should Ordelio do next?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={submitting || !user}
                />
              </div>

              <div className="feedback-actions">
                <button type="submit" className="cta-button" disabled={submitting || !user || syncStatus !== 'online'}>
                  {submitting ? 'Sending…' : 'Send feedback'}
                </button>
              </div>
            </form>
          </section>
        </article>
      </main>

      <footer className="static-footer">
        <p>© 2026 Ordelio. All rights reserved.</p>
      </footer>
    </div>
  )
}
